import { useFormContext } from 'react-hook-form';

import { Category } from '@/__generated_graphql__/graphql';
import { FormValues } from '@/types';

import { useGetCategories } from './useGetCategories';

export const useCategoriesFilter = () => {
  const { data: categories } = useGetCategories();

  const { control, watch, setValue } =
    useFormContext<Pick<FormValues, 'categories'>>();

  const getCategoryById = (id: string | undefined | null) =>
    categories?.find(category => category.id === id);

  const getSelectedState = (
    jobs: Category[],
    selectedCategories: Category[]
  ): { allSelected: boolean; someSelected: boolean } => {
    const allSelected = jobs.every(job =>
      selectedCategories.some(selected => {
        const isParentId = selected.parentId === null;

        return isParentId
          ? String(selected.id) === String(job.parentId)
          : selected.id === job.id;
      })
    );
    const someSelected = jobs.some(job =>
      selectedCategories.some(selected => selected.id === job.id)
    );

    return { allSelected, someSelected };
  };

  const toggleJob = (job: Category, selectedCategories: Category[]) => {
    const isSelected = selectedCategories.some(
      selected => selected.id === job.id
    );

    const { parentId } = job;
    const parentCategory = parentId && getCategoryById(String(parentId));

    const isParentSelected =
      parentCategory &&
      selectedCategories.some(selected => selected.id === parentCategory.id);

    // When parent category was selected, and click on element
    if (isParentSelected) {
      const withoutParent = selectedCategories.filter(
        selected => selected.id !== parentCategory.id
      );

      const childrenToAdd = parentCategory.children.filter(
        child => child.id !== job.id
      );

      return setValue('categories', [...withoutParent, ...childrenToAdd]);
    }

    const updated: Category[] = isSelected
      ? selectedCategories.filter(selected => selected.id !== job.id)
      : [...selectedCategories, job];

    if (parentCategory) {
      const currentCategoryChildren = updated.filter(
        item =>
          item.parentId && String(item.parentId) === String(parentCategory.id)
      );

      const isAllChildrenSelected =
        currentCategoryChildren.length === parentCategory.children.length;

      if (isAllChildrenSelected) {
        const withoutChildren = updated.filter(
          item =>
            !(
              item.parentId &&
              String(item.parentId) === String(parentCategory.id)
            )
        );

        return setValue('categories', [...withoutChildren, parentCategory]);
      }
    }

    setValue('categories', updated);
  };

  const toggleJobsGroup = (
    mainCategory: Category,
    selectedCategories: Category[]
  ) => {
    const deselectCategories = selectedCategories.some(
      selectedCategory => selectedCategory.id === mainCategory.id
    );

    const updated: Category[] = deselectCategories
      ? selectedCategories.filter(item =>
          item.parentId
            ? String(item.parentId) !== String(mainCategory.id)
            : item.id !== mainCategory.id
        )
      : [...selectedCategories, mainCategory].filter(
          items => String(items.parentId) !== String(mainCategory.id)
        );

    setValue('categories', updated);
  };

  const discardJobs = (jobs: Category[], selectedCategories: Category[]) => {
    const filtered = selectedCategories.filter(
      selected => !jobs.some(job => job.id === selected.id)
    );
    setValue('categories', filtered);
  };

  return {
    categories,
    control,
    watch,
    getCategoryById,
    getSelectedState,
    toggleJob,
    toggleJobsGroup,
    discardJobs
  };
};
